import React, { useRef, useState, useEffect } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { ZoomIn, ZoomOut, RotateCcw, Maximize2 } from "lucide-react";

const ZoomableImage = ({
  src,
  alt = "Zoomable image",
  height = "500px",
  className = "",
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isSvg, setIsSvg] = useState(false);

  const containerRef = useRef(null);

  // natural (pixel) size of the asset
  const [natural, setNatural] = useState({ w: 0, h: 0 });

  // initial transform we pass to TW
  const [initial, setInitial] = useState({ scale: 1, x: 0, y: 0 });
  // change key to remount TW (reset)
  const [twKey, setTwKey] = useState(0);

  useEffect(() => {
    if (!src) return;
    const checkIfSvg =
      src.toLowerCase().includes(".svg") ||
      src.toLowerCase().includes("data:image/svg") ||
      src.toLowerCase().includes("svg+xml");
    setIsSvg(checkIfSvg);
    setImageLoaded(false);
  }, [src]);

  const computeFit = () => {
    const el = containerRef.current;
    if (!el || !natural.w || !natural.h) return;

    const cw = el.clientWidth;
    const ch = el.clientHeight;

    // scale to contain natural pixels
    const scale = Math.min(cw / natural.w, ch / natural.h);

    // center the natural-sized content box
    const x = (cw - natural.w * scale) / 2;
    const y = (ch - natural.h * scale) / 2;

    setInitial({ scale, x, y });
  };

  useEffect(() => {
    if (!imageLoaded) return;
    computeFit();
    setTwKey((k) => k + 1); // remount with new initial

    const onResize = () => {
      computeFit();
      setTwKey((k) => k + 1);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageLoaded, isFullscreen, natural.w, natural.h]);

  const containerStyle = {
    ...styles.container,
    ...(isFullscreen ? styles.fullscreenContainer : {}),
    width: "100%",
    height: isFullscreen ? "calc(100vh - 32px)" : height,
  };

  return (
    <>
      {isFullscreen && (
        <div style={styles.fullscreenOverlay} onClick={() => setIsFullscreen(false)} />
      )}

      <div ref={containerRef} style={containerStyle} className={className}>
        <TransformWrapper
          key={twKey}
          initialScale={initial.scale}
          initialPositionX={initial.x}
          initialPositionY={initial.y}
          minScale={0.05}
          maxScale={isSvg ? 20 : 8}
          centerOnInit={false}
          doubleClick={{ disabled: false, mode: "zoomIn", step: 0.7, animationTime: 300 }}
          wheel={{ disabled: true }}
          pinch={{ step: isSvg ? 10 : 5 }}
          panning={{ velocityDisabled: true }}
          limitToBounds={false}
        >
          {({ zoomIn, zoomOut }) => (
            <>
              <div style={styles.controlsContainer}>
                <button onClick={() => zoomIn(0.3)} style={styles.controlButton} title="Zoom In">
                  <ZoomIn style={styles.buttonIcon} />
                </button>
                <button onClick={() => zoomOut(0.3)} style={styles.controlButton} title="Zoom Out">
                  <ZoomOut style={styles.buttonIcon} />
                </button>
                <button
                  onClick={() => setTwKey((k) => k + 1)}
                  style={styles.controlButton}
                  title="Reset View"
                >
                  <RotateCcw style={styles.buttonIcon} />
                </button>
                <button
                  onClick={() => setIsFullscreen((v) => !v)}
                  style={styles.controlButton}
                  title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  <Maximize2 style={styles.buttonIcon} />
                </button>
              </div>

              <TransformComponent
                wrapperStyle={styles.transformWrapper}
                // let the content box define the content size; don't force 100% here
                contentStyle={styles.transformContent}
              >
                {/* 👇 This is the critical part:
                    A "content box" whose size at scale=1 equals the image's natural pixels */}
                <div
                  style={{
                    width: natural.w || 1,
                    height: natural.h || 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={src}
                    alt={alt}
                    draggable={false}
                    // fill the content box
                    style={{ width: "100%", height: "100%", objectFit: "contain", userSelect: "none" }}
                    onLoad={(e) => {
                      const { naturalWidth, naturalHeight, clientWidth, clientHeight } = e.currentTarget;
                      const w = naturalWidth || clientWidth || 1000;
                      const h = naturalHeight || clientHeight || 1000;
                      setNatural({ w, h });
                      setImageLoaded(true);
                    }}
                  />
                </div>
              </TransformComponent>
            </>
          )}
        </TransformWrapper>

        <div style={styles.helpIndicator}>
          <div style={styles.helpText}>
            <span style={styles.helpEmoji}>✨</span>
            <span>Double-click to zoom • Use buttons • Drag to pan</span>
          </div>
        </div>

        {isFullscreen && (
          <button
            onClick={() => setIsFullscreen(false)}
            style={styles.closeButton}
            title="Exit Fullscreen"
          >
            <svg style={styles.closeIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </>
  );
};

const styles = {
  container: {
    position: "relative",
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
    border: "2px solid rgba(255,255,255,0.5)",
    background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 50%, #f3e8ff 100%)",
  },
  fullscreenContainer: {
    position: "fixed", top: 16, left: 16, right: 16, bottom: 16, zIndex: 50,
    background: "linear-gradient(135deg, #111827 0%, #1e3a8a 50%, #581c87 100%)",
    border: "2px solid rgba(255,255,255,0.2)",
  },
  fullscreenOverlay: {
    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)", zIndex: 40,
  },
  controlsContainer: {
    position: "absolute", top: 24, right: 24, zIndex: 10,
    display: "flex", flexDirection: "column", gap: 12,
    backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)",
    borderRadius: 16, padding: 12,
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", border: "2px solid rgba(255,255,255,0.6)",
  },
  controlButton: {
    padding: 12, borderRadius: 12, border: "2px solid rgba(255,255,255,0.3)",
    cursor: "pointer", transition: "all 0.3s ease",
    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)", outline: "none",
    background: "linear-gradient(135deg, #ea6347 0%, #ea6347 100%)", color: "white",
  },
  buttonIcon: { width: 20, height: 20 },
  transformWrapper: { width: "100%", height: "100%" },
  transformContent: {
    width: "100%",
    height: "100%",
    // don’t center here; the content box itself has the natural size
  },
  helpIndicator: {
    position: "absolute", bottom: 24, left: 24,
    backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)",
    borderRadius: 16, padding: "12px 16px",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)", border: "2px solid rgba(255,255,255,0.6)",
  },
  helpText: { fontSize: 14, color: "#374151", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 },
  helpEmoji: { fontSize: 18 },
  closeButton: {
    position: "absolute", top: 24, left: 24, zIndex: 10, padding: 16,
    background: "linear-gradient(135deg, rgba(31,41,55,0.8) 0%, rgba(17,24,39,0.8) 100%)",
    backdropFilter: "blur(20px)", borderRadius: 16, color: "white",
    transition: "all 0.3s ease", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
    border: "2px solid rgba(255,255,255,0.2)", cursor: "pointer", outline: "none",
  },
  closeIcon: { width: 24, height: 24 },
};

export default ZoomableImage;
